import { TbEye } from "react-icons/tb";
import intl from "react-intl-universal";

const COL_SPAN_CLASS = {
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
};

/** Column header row — styles live in `.grid-history-header` (index.css). */
export const GRID_HISTORY_HEADER_CLASS = "grid-history-header";

const HEADER_BASE_CLASS = `${GRID_HISTORY_HEADER_CLASS} hidden lg:-mx-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:self-start lg:gap-x-4 lg:px-4 lg:min-h-12 lg:py-2`;

const ROW_BASE_CLASS =
  "flex flex-col gap-2 border-b border-gray-100 px-4 py-2.5 last:border-b-0 dark:border-brand-dark-border lg:grid lg:grid-cols-subgrid lg:items-center lg:self-start lg:gap-x-4 lg:px-0 lg:py-2";

export const GRID_HISTORY_COLS_3 =
  "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]";

/** Wrap nested rows so subgrid inherits parent column tracks (e.g. day groups in Extract). */
export const GRID_HISTORY_SUBGRID_SECTION = "col-span-3 lg:grid lg:grid-cols-subgrid";

/** Transaction | date | value | status + actions (Crossborder-style generous spacing). */
export const GRID_HISTORY_COLS_4 =
  "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)]";

export const GRID_HISTORY_COLS_5 =
  "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1.2fr)]";

/** Entity | primary | meta | amount | date | status + actions (BaaS accounts-style). */
export const GRID_HISTORY_COLS_6 =
  "lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,1.2fr)]";

/** Entity | account | type | balances | created | status | actions (Contas operacional). */
export const GRID_HISTORY_COLS_7 =
  "lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1.5fr)_minmax(0,0.85fr)_minmax(0,1.2fr)_minmax(0,0.85fr)_minmax(0,1.15fr)_minmax(0,0.8fr)]";

/** Entity | account | type | balances | created | status conta | status proposta | actions. */
export const GRID_HISTORY_COLS_8 =
  "lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1.3fr)_minmax(0,0.75fr)_minmax(0,1.05fr)_minmax(0,0.75fr)_minmax(0,0.9fr)_minmax(0,1.45fr)_minmax(0,0.85fr)]";

/** Entity | account | type | instance | balances | created | status conta | status proposta | actions. */
export const GRID_HISTORY_COLS_9 =
  "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.15fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_minmax(0,0.95fr)_minmax(0,0.65fr)_minmax(0,0.8fr)_minmax(0,1.25fr)_minmax(0,0.75fr)]";

/** Right-aligned value header (pairs with GridHistoryCell align="right"). */
export const GRID_HISTORY_HEADER_VALUE_CLASS = "text-right";

/** List card: hug rows; pagination sits under the last row (no viewport min-height). */
export const GridHistoryCard = ({ children, className = "" }) => (
  <div className={`grid-history-card app-card overflow-hidden ${className}`.trim()}>{children}</div>
);

export const GridHistoryContainer = ({ gridCols, children, className = "" }) => (
  <div
    className={`h-auto min-h-0 w-full lg:grid lg:content-start lg:items-start ${gridCols} lg:gap-x-4 lg:px-4 ${className}`}
  >
    {children}
  </div>
);

export const GridHistoryHeader = ({ colSpan = 5, children, className = "" }) => (
  <div className={`${COL_SPAN_CLASS[colSpan] ?? "col-span-5"} ${HEADER_BASE_CLASS} ${className}`}>
    {children}
  </div>
);

export const GridHistoryRow = ({
  colSpan = 5,
  children,
  className = "",
  onClick,
  role,
  tabIndex,
  onKeyDown,
}) => (
  <div
    className={`${COL_SPAN_CLASS[colSpan] ?? "col-span-5"} ${ROW_BASE_CLASS} ${className}`}
    onClick={onClick}
    role={role}
    tabIndex={tabIndex}
    onKeyDown={onKeyDown}
  >
    {children}
  </div>
);

export const GridHistoryCell = ({ label, children, align = "left", className = "" }) => (
  <div className={`min-w-0 ${className}`.trim()}>
    <p
      className={`mb-px text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : ""
      }`}
    >
      {label}
    </p>
    <div
      className={
        align === "right" ? "text-right" : align === "center" ? "flex justify-center" : ""
      }
    >
      {children}
    </div>
  </div>
);

export const GridHistoryDetailButton = ({ onClick }) => (
  <button
    type="button"
    className="grid-history-action-btn !h-8 !min-h-8 shrink-0 !px-3"
    onClick={(event) => {
      event.stopPropagation();
      onClick?.(event);
    }}
    aria-label={intl.get("common.details")}
  >
    <TbEye className="h-3.5 w-3.5" />
    {intl.get("common.details")}
  </button>
);

export const GridHistoryStatusActionsHeader = ({ statusLabel }) => (
  <div className="flex items-center justify-end gap-2.5">
    <span>{statusLabel}</span>
    <div className="invisible shrink-0" aria-hidden="true">
      <GridHistoryDetailButton onClick={() => {}} />
    </div>
  </div>
);

export const GridHistoryStatusActionsCell = ({ statusLabel, statusBadge, onShowDetail, item }) => (
  <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
    <p className="mb-px w-full text-xs font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
      {statusLabel}
    </p>
    {statusBadge}
    <GridHistoryDetailButton onClick={() => onShowDetail(item)} />
  </div>
);

/** Standalone header for tables where rows are not in a subgrid parent (e.g. mixed row types). */
export const GridHistoryStandaloneHeader = ({ gridCols, children, className = "" }) => (
  <div
    className={`${GRID_HISTORY_HEADER_CLASS} hidden lg:grid ${gridCols} lg:min-h-12 lg:items-center lg:gap-x-4 lg:px-4 lg:py-2 ${className}`}
  >
    {children}
  </div>
);

export const GRID_HISTORY_COLS_EXTRACT =
  "lg:grid-cols-[minmax(0,2fr)_minmax(0,0.75fr)_minmax(0,1fr)]";
